/**
 * Manage Add to Cart Button Action
 * Triggered by: arc:action:manageAddButton
 */

export const runManageAddButton = async (data = {}) => {
    try {
        const { 
            action = 'update', // 'update', 'show', 'hide', 'showBackInStock', 'hideBackInStock'
            disabled = false, 
            text = '', 
            modifyClass = true, 
            basePrice = 0, 
            targetForm = null 
        } = data;

        let result = {};

        switch (action) {
            case 'update':
                result = await updateAddButtonDom(disabled, text, modifyClass, basePrice, targetForm);
                break;
            case 'show':
                result = await showAddButton(targetForm);
                break;
            case 'hide':
                result = await hideAddButton(targetForm);
                break;
            case 'showBackInStock':
                result = await showBackInStockForm();
                break;
            case 'hideBackInStock':
                result = await hideBackInStockForm();
                break;
            default:
                throw new Error(`Unknown action: ${action}`);
        }

        // Dispatch button state change event
        window.dispatchEvent(new CustomEvent("addButton:stateChanged", {
            detail: {
                action,
                disabled,
                text,
                basePrice,
                timestamp: Date.now()
            }
        }));

        return {
            success: true,
            action,
            message: `Add button ${action} completed successfully`,
            ...result
        };

    } catch (error) {
        console.error('Manage Add Button error:', error);
        
        window.dispatchEvent(new CustomEvent('addButton:error', {
            detail: {
                error: error.message,
                action: 'manageAddButton'
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Store selected base price globally
let selectedBasePrice = 0;

// Helper function to get product forms
const getProductForms = (form = null) => {
    if (form) {
        return [form];
    }
    return document.querySelectorAll('.pdp-details .product-form');
};

// Update add button DOM
const updateAddButtonDom = async (disable = true, text = '', modifyClass = true, basePrice = 0, form = null) => {
    const productForms = getProductForms(form);
    
    // Show button and hide back in stock form first
    await showAddButton(form);
    await hideBackInStockForm();
    
    if (basePrice > 0) {
        selectedBasePrice = basePrice;
    }

    productForms.forEach((productForm) => {
        if (!productForm) return;
        
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        
        if (!addButton) return;

        if (disable) {
            addButton.setAttribute("disabled", "disabled");
            addButton.querySelector("span")?.setAttribute(
                "x-on:click",
                "$nextTick(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); })"
            );
            if (text) addButtonText.textContent = text;
        } else {
            addButton.removeAttribute("disabled");
            if (text) addButtonText.textContent = text;
        }

        if (modifyClass) {
            if (disable) {
                addButton.classList.add("disabled");
            } else {
                addButton.classList.remove("disabled");
            }
        }
    });

    return { disabled: disable, text, basePrice: selectedBasePrice };
};

// Show add button
const showAddButton = async (form = null) => {
    const productForms = getProductForms(form);
    
    productForms.forEach((productForm) => {
        if (!productForm) return;
        
        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        addButton.style.display = 'inline-flex';
    });

    return { visible: true };
};

// Hide add button
const hideAddButton = async (form = null) => {
    const productForms = getProductForms(form);
    
    productForms.forEach((productForm) => {
        if (!productForm) return;
        
        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        addButton.style.display = 'none';
    });

    return { visible: false };
};

// Show back in stock form
const showBackInStockForm = async () => {
    const backInStockForm = document.querySelector('.pdp-details .outofstock-group');
    if (backInStockForm) {
        backInStockForm.style.display = 'block';
        return { visible: true };
    }
    return { visible: false };
};

// Hide back in stock form
const hideBackInStockForm = async () => {
    const backInStockForm = document.querySelector('.pdp-details .outofstock-group');
    if (backInStockForm) {
        backInStockForm.style.display = 'none';
        return { visible: false };
    }
    return { visible: false };
};

// Getter functions for external access
export const getCurrentVariant = () => {
    return window.currentVariant || null;
};

export const getSelectedBasePrice = () => {
    return selectedBasePrice;
};

// Set up event listeners for the new events
export const initializeAddButtonEventListeners = () => {
    window.addEventListener('addButton:update', (event) => {
        const { disabled, text, modifyClass, price, targetForm } = event.detail;
        runManageAddButton({
            action: 'update',
            disabled,
            text,
            modifyClass,
            basePrice: price,
            targetForm
        });
    });

    window.addEventListener('addButton:show', (event) => {
        const { targetForm } = event.detail;
        runManageAddButton({
            action: 'show',
            targetForm
        });
    });

    window.addEventListener('addButton:hide', (event) => {
        const { targetForm } = event.detail;
        runManageAddButton({
            action: 'hide',
            targetForm
        });
    });

    window.addEventListener('backInStock:show', () => {
        runManageAddButton({
            action: 'showBackInStock'
        });
    });

    window.addEventListener('backInStock:hide', () => {
        runManageAddButton({
            action: 'hideBackInStock'
        });
    });
};

export default runManageAddButton;
